'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2, MessageSquare } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/shadcn/popover';
import { toast } from '@/shared/lib/toast';
import { cn } from '@/shared/lib/cn';
import { supportService } from '../api/support.service';
import { MAX_MESSAGE_LENGTH, MIN_MESSAGE_LENGTH } from '../types';

/**
 * Always-available "Feedback" header action for wide screens (tablet / desktop).
 *
 * It is the SAME support-ticket mechanism as the full form in Account →
 * Feedback (`FeedbackForm` → `supportService.submitFeedback`), reduced to the
 * one field that matters for a drive-by remark: the message. The category is
 * pinned to `general_inquiry` and attachments are omitted — the full form stays
 * the place for a categorized report with screenshots.
 *
 * Hidden below `md` on purpose: on phones the header has no room for a labelled
 * action, and the Account entry point already covers that case. Mirrors Apple's
 * `feedbackToolbarItem()` and Android's `FeedbackTopBarAction()`.
 */
export function FeedbackHeaderButton({
  variant = 'default',
  className,
}: {
  /**
   * `hero` renders the dark-glass pill used over the building page's teal wash,
   * matching the ⋮ button it sits next to (see `ProjectActionsMenu`).
   */
  variant?: 'default' | 'hero';
  className?: string;
}) {
  const t = useTranslations('feedback');
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit =
    message.trim().length >= MIN_MESSAGE_LENGTH && !isSubmitting;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Reset on close so a reopened popover is always a fresh note.
    if (!next) setMessage('');
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await supportService.submitFeedback({
        category: 'general_inquiry',
        message: message.trim(),
      });
      // Confirmation is the app's standard toast — the same one the full
      // Account → Feedback form shows. The popover closes right away rather
      // than holding a second, popover-local success panel: two confirmations
      // read as "sent twice". Apple and Android do the same.
      handleOpenChange(false);
      toast.success(t('form.success.toast'));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('form.errors.submitFailed')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          // Wide screens only — see the component doc.
          className={cn(
            'hidden md:inline-flex h-9',
            variant === 'hero' &&
              'bg-background/60 backdrop-blur-sm border-transparent text-foreground hover:bg-background/80',
            className
          )}
        >
          <MessageSquare className="h-4 w-4" />
          {t('quick.button')}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[360px] p-3">
        <div className="space-y-3">
          <Textarea
            autoFocus
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('quick.placeholder')}
            maxLength={MAX_MESSAGE_LENGTH}
            disabled={isSubmitting}
            rows={5}
            className="resize-none min-h-[120px]"
          />
          <div className="flex items-end justify-between gap-3">
            <p className="text-form-help text-muted-foreground">
              {t('form.reassurance')}
            </p>
            <Button
              type="button"
              size="sm"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('form.actions.submitting')}
                </>
              ) : (
                t('form.actions.submit')
              )}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
